import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import './VideoGenerator.css';

const PROMPT_EXAMPLES = [
  'A golden sunrise over a calm ocean with gentle waves lapping at a sandy shore, warm light reflecting on the water',
  'A futuristic city at night with neon lights, flying cars, and holographic billboards illuminating busy streets',
  'A serene forest path in autumn, leaves falling gently as sunlight filters through colorful canopy',
  'An astronaut floating in space with Earth in the background, stars twinkling in the vast cosmic expanse',
  'A cozy coffee shop on a rainy day, steam rising from cups, raindrops running down the window glass',
];

function VideoGenerator({ onGenerate, dimensions }) {
  const { credits, isAdmin, refreshCredits } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [dimension, setDimension] = useState('1280*720');
  const [duration, setDuration] = useState(30);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeGenerations, setActiveGenerations] = useState([]);

  // Credit request modal
  const [showCreditRequest, setShowCreditRequest] = useState(false);
  const [creditAmount, setCreditAmount] = useState(10);
  const [creditReason, setCreditReason] = useState('');
  const [requestingCredits, setRequestingCredits] = useState(false);

  const handleTryExample = () => {
    const random = PROMPT_EXAMPLES[Math.floor(Math.random() * PROMPT_EXAMPLES.length)];
    setPrompt(random);
  };

  const handleRequestCredits = async () => {
    if (!creditReason.trim() || creditAmount < 1) return;
    setRequestingCredits(true);
    try {
      await authService.requestCredits(creditAmount, creditReason);
      setMessage({ type: 'success', text: 'Credit request submitted! Waiting for admin approval.' });
      setShowCreditRequest(false);
      setCreditAmount(10);
      setCreditReason('');
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to submit credit request' });
    } finally {
      setRequestingCredits(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!prompt.trim()) {
      setMessage({ type: 'error', text: 'Please enter a video description' });
      return;
    }

    if (prompt.length < 10) {
      setMessage({ type: 'error', text: 'Description must be at least 10 characters' });
      return;
    }

    setIsGenerating(true);
    setMessage(null);

    try {
      const result = await onGenerate(prompt, dimension, duration);

      setActiveGenerations(prev => [
        ...prev,
        {
          jobId: result.job_id,
          prompt: prompt.substring(0, 60) + (prompt.length > 60 ? '...' : ''),
          duration,
          dimension,
          startTime: new Date(),
        },
      ]);

      setMessage({
        type: 'success',
        text: 'Video generation started! Head to "All Jobs" to track progress.',
      });

      setPrompt('');
      refreshCredits();
    } catch (error) {
      let errorMessage = 'Failed to start video generation';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsGenerating(false);
    }
  };

  const getDimensionLabel = (dim) => {
    switch (dim) {
      case '1280*720': return '1280 × 720';
      case '1024*576': return '1024 × 576';
      case '768*432': return '768 × 432';
      default: return dim;
    }
  };

  return (
    <div className="video-generator">
      {/* Hero Section */}
      <div className="vg-hero">
        <div className="vg-hero-badge">
          <i className="fas fa-sparkles"></i>
          AI-Powered Video Creation
        </div>
        <h1 className="vg-hero-title">Create Stunning Videos</h1>
        <p className="vg-hero-subtitle">
          Describe your vision and let AI bring it to life with cinematic quality video generation
        </p>
      </div>

      {/* Credits Bar */}
      {!isAdmin && (
        <div className={`vg-credits-bar ${credits === 0 ? 'vg-credits-empty' : ''}`}>
          <div className="vg-credits-info">
            <i className="fas fa-bolt"></i>
            <span className="vg-credits-count">{credits}</span>
            <span className="vg-credits-label">credits remaining</span>
          </div>
          {credits === 0 && (
            <button
              className="xbtn xbtn-sm xbtn-primary"
              onClick={() => setShowCreditRequest(true)}
            >
              <i className="fas fa-hand-holding-heart"></i>
              Request Credits
            </button>
          )}
          {credits > 0 && credits <= 3 && (
            <button
              className="xbtn xbtn-sm xbtn-secondary"
              onClick={() => setShowCreditRequest(true)}
            >
              Request More
            </button>
          )}
        </div>
      )}

      {/* Active Generations */}
      {activeGenerations.length > 0 && (
        <div className="vg-active-bar">
          <div className="vg-active-label">
            <i className="fas fa-circle-notch fa-spin"></i>
            <span>{activeGenerations.length} generation{activeGenerations.length > 1 ? 's' : ''} in progress</span>
          </div>
          <div className="vg-active-items">
            {activeGenerations.map((gen) => (
              <div key={gen.jobId} className="vg-active-chip">
                <span className="vg-chip-text">{gen.prompt}</span>
                <span className="vg-chip-meta">{gen.duration}s</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Form Card */}
      <form onSubmit={handleSubmit} className="vg-form-card">
        {/* Prompt Section */}
        <div className="vg-prompt-section">
          <div className="vg-prompt-header">
            <label htmlFor="prompt">
              <i className="fas fa-pen-fancy"></i>
              Describe Your Video
            </label>
            <button
              type="button"
              className="xbtn xbtn-sm xbtn-secondary vg-example-btn"
              onClick={handleTryExample}
              disabled={isGenerating}
            >
              <i className="fas fa-dice"></i>
              Try an example
            </button>
          </div>
          <div className="vg-prompt-wrapper">
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g. A cinematic aerial shot of a mountain landscape at golden hour, with clouds rolling through valleys and sunlight casting long shadows..."
              rows="5"
              disabled={isGenerating}
              maxLength={2000}
            />
            <div className="vg-prompt-footer">
              <span className="vg-char-count">{prompt.length} / 2000</span>
            </div>
          </div>
        </div>

        {/* Settings Row */}
        <div className="vg-settings">
          <div className="vg-setting-group">
            <label>
              <i className="fas fa-expand-alt"></i>
              Resolution
            </label>
            <div className="vg-radio-group">
              {(dimensions.length > 0 ? dimensions : ['1280*720', '1024*576', '768*432']).map(
                (dim) => (
                  <button
                    key={dim}
                    type="button"
                    className={`vg-radio-btn ${dimension === dim ? 'active' : ''}`}
                    onClick={() => setDimension(dim)}
                    disabled={isGenerating}
                  >
                    {getDimensionLabel(dim)}
                    {dim === '1280*720' && <span className="vg-rec-tag">HD</span>}
                  </button>
                )
              )}
            </div>
          </div>

          <div className="vg-setting-group">
            <label>
              <i className="fas fa-clock"></i>
              Duration
            </label>
            <div className="vg-radio-group">
              <button
                type="button"
                className={`vg-radio-btn ${duration === 30 ? 'active' : ''}`}
                onClick={() => setDuration(30)}
                disabled={isGenerating}
              >
                30 seconds
                <span className="vg-est-tag">~4 min</span>
              </button>
              <button
                type="button"
                className={`vg-radio-btn ${duration === 40 ? 'active' : ''}`}
                onClick={() => setDuration(40)}
                disabled={isGenerating}
              >
                40 seconds
                <span className="vg-est-tag">~6 min</span>
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`vg-message vg-message-${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            <span>{message.text}</span>
          </div>
        )}

        {/* Submit */}
        <button type="submit" className="xbtn xbtn-xl xbtn-primary vg-submit-btn" disabled={isGenerating || prompt.length < 10 || (!isAdmin && credits < 1)}>
          {isGenerating ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i>
              Starting Generation...
            </>
          ) : (!isAdmin && credits < 1) ? (
            <>
              <i className="fas fa-lock"></i>
              No Credits — Request More
            </>
          ) : (
            <>
              <i className="fas fa-wand-magic-sparkles"></i>
              Generate Video
            </>
          )}
        </button>
      </form>

      {/* Feature Cards */}
      <div className="vg-features">
        <div className="vg-feature-card">
          <div className="vg-feature-icon">
            <i className="fas fa-bolt"></i>
          </div>
          <h4>Fast Generation</h4>
          <p>Videos ready in minutes with high quality output</p>
        </div>
        <div className="vg-feature-card">
          <div className="vg-feature-icon vg-feature-icon-purple">
            <i className="fas fa-layer-group"></i>
          </div>
          <h4>Multi-clip Stitching</h4>
          <p>Seamlessly combines scenes for longer videos</p>
        </div>
        <div className="vg-feature-card">
          <div className="vg-feature-icon vg-feature-icon-green">
            <i className="fas fa-photo-film"></i>
          </div>
          <h4>HD Quality</h4>
          <p>Up to 1280×720 resolution with smooth motion</p>
        </div>
        <div className="vg-feature-card">
          <div className="vg-feature-icon vg-feature-icon-orange">
            <i className="fas fa-infinity"></i>
          </div>
          <h4>Parallel Jobs</h4>
          <p>Run multiple generations at the same time</p>
        </div>
      </div>

      {/* Credit Request Modal */}
      {showCreditRequest && (
        <div className="vg-modal-overlay" onClick={() => setShowCreditRequest(false)}>
          <div className="vg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vg-modal-header">
              <h3><i className="fas fa-bolt"></i> Request Credits</h3>
              <button className="vg-modal-close" onClick={() => setShowCreditRequest(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="vg-modal-body">
              <div className="vg-modal-field">
                <label>Number of Credits</label>
                <input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  min={1}
                  max={100}
                />
                <span className="vg-field-hint">1 credit = 1 video generation</span>
              </div>
              <div className="vg-modal-field">
                <label>Reason</label>
                <textarea
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="Briefly explain why you need more credits..."
                  rows={3}
                  required
                />
              </div>
            </div>
            <div className="vg-modal-actions">
              <button className="xbtn xbtn-md xbtn-secondary" onClick={() => setShowCreditRequest(false)}>
                Cancel
              </button>
              <button
                className="xbtn xbtn-md xbtn-primary"
                onClick={handleRequestCredits}
                disabled={requestingCredits || !creditReason.trim()}
              >
                {requestingCredits ? (
                  <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Submit Request</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoGenerator;
